import { Particle } from '../ParticleLayer';

const CONFIG = {
    scale: 0.9,
    riseDuration: 500,
    gatherDuration: 500,
    strikeDuration: 200,
    shockwaveDuration: 500,
    dissipateDuration: 600,
    leafCount: 40,
    emberCount: 40,
    paths: {
        sword: '/assets/effects/ancient_sword/sword.svg',
    }
};

const spriteCache: Record<string, HTMLImageElement> = {};
const getSprite = (src: string): HTMLImageElement => {
    if (!spriteCache[src]) {
        const img = new Image();
        img.src = src;
        spriteCache[src] = img;
    }
    return spriteCache[src];
};

getSprite(CONFIG.paths.sword);

// ==========================================
// PARTICLES
// ==========================================

class AncientLeaf {
    x: number; y: number;
    vx: number; vy: number;
    life: number; size: number;
    color: string;
    rotation: number;
    rotSpeed: number;

    constructor(startX: number, startY: number) {
        this.x = startX;
        this.y = startY;
        const speed = 4 + Math.random() * 8;
        const angle = Math.random() * Math.PI * 2;
        this.vx = Math.cos(angle) * speed;
        this.vy = Math.sin(angle) * speed - 2;
        this.life = 1.0;
        this.size = 3 + Math.random() * 6;

        const colors = ['#00FFFF', '#E0FFFF', '#FFE066', '#d4af37'];
        this.color = colors[Math.floor(Math.random() * colors.length)];

        this.rotation = Math.random() * Math.PI * 2;
        this.rotSpeed = (Math.random() - 0.5) * 0.4;
    }

    update(dt: number) {
        this.vy += 0.1; // Gentle Gravity
        // Sway like a leaf
        this.vx += Math.sin(this.life * 10) * 0.5;
        this.x += this.vx;
        this.y += this.vy;
        this.vx *= 0.92;
        this.vy *= 0.92;
        this.life -= 0.012;
        this.rotation += this.rotSpeed;
    }

    draw(ctx: CanvasRenderingContext2D) {
        if (this.life <= 0) return;
        ctx.save();
        ctx.globalAlpha = Math.max(0, this.life);
        ctx.globalCompositeOperation = 'screen';
        ctx.translate(this.x, this.y);
        ctx.rotate(this.rotation);
        ctx.fillStyle = this.color;
        ctx.shadowColor = this.color;
        ctx.shadowBlur = 10 * this.life;

        // Draw as tiny diamonds / leaves
        ctx.beginPath();
        ctx.moveTo(0, -this.size);
        ctx.lineTo(this.size / 2, 0);
        ctx.lineTo(0, this.size);
        ctx.lineTo(-this.size / 2, 0);
        ctx.closePath();
        ctx.fill();
        ctx.restore();
    }
}

class CelestialSwirl {
    x: number; y: number;
    startX: number; startY: number;
    targetX: number; targetY: number;
    delay: number; duration: number;
    lifeTime: number = 0;
    size: number;
    color: string;

    constructor(targetX: number, targetY: number) {
        const angle = Math.random() * Math.PI * 2;
        const dist = 200 + Math.random() * 200;
        this.startX = targetX + Math.cos(angle) * dist;
        this.startY = targetY + Math.sin(angle) * dist;
        this.x = this.startX;
        this.y = this.startY;
        this.targetX = targetX;
        this.targetY = targetY;

        this.delay = Math.random() * 300;
        this.duration = 400 + Math.random() * 300;
        this.size = 2 + Math.random() * 4;

        const colors = ['#00FFFF', '#E0FFFF', '#0088FF'];
        this.color = colors[Math.floor(Math.random() * colors.length)];
    }

    update(dt: number): boolean {
        if (this.delay > 0) {
            this.delay -= dt;
            return true;
        }
        this.lifeTime += dt;
        let progress = this.lifeTime / this.duration;
        if (progress >= 1) progress = 1;

        const ease = Math.pow(progress, 2);

        this.x = this.startX + (this.targetX - this.startX) * ease;
        this.y = this.startY + (this.targetY - this.startY) * ease;

        const swirlAngle = progress * Math.PI * 4;
        const swirlRadius = (1 - progress) * 60;
        this.x += Math.cos(swirlAngle) * swirlRadius;
        this.y += Math.sin(swirlAngle) * swirlRadius;

        return progress < 1;
    }

    draw(ctx: CanvasRenderingContext2D) {
        if (this.delay > 0) return;
        ctx.save();
        ctx.globalCompositeOperation = 'screen';
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fillStyle = this.color;
        ctx.shadowColor = this.color;
        ctx.shadowBlur = 8;
        ctx.fill();
        ctx.restore();
    }
}

// ==========================================
// EFFECT STATE & RENDERER
// ==========================================

export type AncientSwordEffect = {
    kind: 'ancientSword'
    t: number
    sx: number; sy: number; // Caster
    tx: number; ty: number; // Target
    data?: {
        phase: 'UNEARTH' | 'GATHER' | 'STRIKE' | 'SHOCKWAVE' | 'DISSIPATE'
        motes: CelestialSwirl[]
        leaves: AncientLeaf[]
        attackAngle: number
        hoverX: number
        hoverY: number
    }
}

export const createAncientSwordEffect = (sx: number, sy: number, tx: number, ty: number): AncientSwordEffect => ({
    kind: 'ancientSword', t: 0, sx, sy, tx, ty
});

export const renderAncientSwordEffect = (
    eff: AncientSwordEffect,
    ctx: CanvasRenderingContext2D,
    spawnParticle: (p: Particle) => void
): boolean => {
    if (!eff.data) {
        const dx = eff.tx - eff.sx;
        const dy = eff.ty - eff.sy;
        const attackAngle = Math.atan2(dy, dx);

        // Hover point slightly behind and above the caster
        const hoverX = eff.sx - Math.cos(attackAngle) * 50;
        const hoverY = eff.sy - Math.sin(attackAngle) * 50 - 150;

        const motes: CelestialSwirl[] = [];
        for (let i = 0; i < 50; i++) {
            motes.push(new CelestialSwirl(hoverX, hoverY));
        }

        eff.data = {
            phase: 'UNEARTH',
            motes,
            leaves: [],
            attackAngle,
            hoverX,
            hoverY
        };
    }

    const d = eff.data;
    const dt = 16.7;

    const tUnearth = CONFIG.riseDuration;
    const tGather = tUnearth + CONFIG.gatherDuration;
    const tStrike = tGather + CONFIG.strikeDuration;
    const tShockwave = tStrike + CONFIG.shockwaveDuration;
    const tTotal = tShockwave + CONFIG.dissipateDuration;

    // Update Phase
    if (eff.t < tUnearth) d.phase = 'UNEARTH';
    else if (eff.t < tGather) d.phase = 'GATHER';
    else if (eff.t < tStrike) d.phase = 'STRIKE';
    else if (eff.t < tShockwave) d.phase = 'SHOCKWAVE';
    else d.phase = 'DISSIPATE';

    ctx.save();

    // 1. Camera Shake (Impact)
    let shakeX = 0;
    let shakeY = 0;
    if (d.phase === 'SHOCKWAVE') {
        const shakeIntensity = 1 - ((eff.t - tStrike) / CONFIG.shockwaveDuration);
        if (shakeIntensity > 0) {
            shakeX = (Math.random() - 0.5) * 30 * Math.pow(shakeIntensity, 2);
            shakeY = (Math.random() - 0.5) * 30 * Math.pow(shakeIntensity, 2);
        }
    }
    ctx.translate(shakeX, shakeY);

    // ==========================================
    // RENDER: SWORD
    // ==========================================
    let swordAlpha = 0;
    let swordScale = 0.3;
    let currentX = eff.sx;
    let currentY = eff.sy;
    let glowIntensity = 0;
    let drawTrail = false;
    let trailOpacity = 0;

    // The SVG is drawn upright, pointing down is rotation 0
    const swordRotation = d.attackAngle - Math.PI / 2;

    if (d.phase === 'UNEARTH') {
        const p = eff.t / tUnearth;
        swordAlpha = Math.min(1, p * 2.5); // fade in quickly
        const easeOut = 1 - Math.pow(1 - p, 4);

        // Rises from caster's position to hover position
        currentX = eff.sx + (d.hoverX - eff.sx) * easeOut;
        currentY = eff.sy + (d.hoverY - eff.sy) * easeOut + (1 - easeOut) * 100; // comes from below
        swordScale = 0.3 + (CONFIG.scale - 0.3) * easeOut;
        glowIntensity = p * 15;

    } else if (d.phase === 'GATHER') {
        swordAlpha = 1;
        swordScale = CONFIG.scale;
        const p = (eff.t - tUnearth) / CONFIG.gatherDuration;

        // Minimal float
        const floatY = Math.sin(p * Math.PI * 2) * 5;
        currentX = d.hoverX;
        currentY = d.hoverY + floatY;
        glowIntensity = 15 + p * 25;

        // Render gathering motes
        d.motes = d.motes.filter(m => m.update(dt));
        d.motes.forEach(m => m.draw(ctx));

    } else if (d.phase === 'STRIKE') {
        swordAlpha = 1;
        swordScale = CONFIG.scale;
        const p = (eff.t - tGather) / CONFIG.strikeDuration;
        const easeIn = Math.pow(p, 3); // Accelerate into target

        currentX = d.hoverX + (eff.tx - d.hoverX) * easeIn;
        currentY = d.hoverY + (eff.ty - d.hoverY) * easeIn;
        glowIntensity = 40;

        drawTrail = true;
        trailOpacity = 1;

    } else if (d.phase === 'SHOCKWAVE') {
        swordAlpha = 1;
        swordScale = CONFIG.scale;
        currentX = eff.tx;
        currentY = eff.ty;
        glowIntensity = 30;

        // Spawn sparks once on impact
        if (d.leaves.length === 0) {
            for (let i = 0; i < 20; i++) {
                spawnParticle({
                    x: eff.tx,
                    y: eff.ty,
                    vx: (Math.random() - 0.5) * 25,
                    vy: (Math.random() - 0.5) * 25,
                    life: 400 + Math.random() * 400,
                    color: Math.random() > 0.5 ? '#00FFFF' : '#FFE066',
                    size: 2 + Math.random() * 5
                });
            }
            // Spawn leaves
            for (let i = 0; i < CONFIG.leafCount; i++) {
                d.leaves.push(new AncientLeaf(eff.tx, eff.ty));
            }
        }

        const shockT = eff.t - tStrike;
        if (shockT < 150) {
            drawTrail = true;
            trailOpacity = 1 - (shockT / 150);
        }

    } else if (d.phase === 'DISSIPATE') {
        const p = (eff.t - tShockwave) / CONFIG.dissipateDuration;
        swordAlpha = 1 - p;
        if (swordAlpha < 0) swordAlpha = 0;
        swordScale = CONFIG.scale;
        currentX = eff.tx;
        currentY = eff.ty;
        glowIntensity = 30 * (1 - p);

        // Generate shattering leaves if missed
        if (d.leaves.length === 0) {
            for (let i = 0; i < CONFIG.leafCount; i++) {
                d.leaves.push(new AncientLeaf(eff.tx, eff.ty));
            }
        }
    }

    // Draw the Trail
    if (drawTrail && trailOpacity > 0) {
        ctx.save();
        ctx.globalAlpha = trailOpacity;
        ctx.globalCompositeOperation = 'screen';

        const gradient = ctx.createLinearGradient(d.hoverX, d.hoverY, currentX, currentY);
        gradient.addColorStop(0, 'rgba(0, 255, 255, 0)');
        gradient.addColorStop(0.5, 'rgba(0, 136, 255, 0.4)');
        gradient.addColorStop(1, 'rgba(0, 255, 255, 0.8)');

        ctx.beginPath();
        // Width of the trail
        const trailWidth = 20 * CONFIG.scale;
        const dx = currentX - d.hoverX;
        const dy = currentY - d.hoverY;
        const len = Math.hypot(dx, dy);

        if (len > 0) {
            const nx = -dy / len;
            const ny = dx / len;

            ctx.moveTo(d.hoverX + nx * trailWidth * 0.1, d.hoverY + ny * trailWidth * 0.1);
            ctx.lineTo(currentX + nx * trailWidth, currentY + ny * trailWidth);
            ctx.lineTo(currentX - nx * trailWidth, currentY - ny * trailWidth);
            ctx.lineTo(d.hoverX - nx * trailWidth * 0.1, d.hoverY - ny * trailWidth * 0.1);
            ctx.closePath();

            ctx.fillStyle = gradient;
            ctx.shadowColor = '#00FFFF';
            ctx.shadowBlur = 20;
            ctx.fill();
        }
        ctx.restore();
    }

    // Draw the Sword SVG
    if (swordAlpha > 0) {
        ctx.save();
        ctx.globalAlpha = swordAlpha;
        ctx.translate(currentX, currentY);
        ctx.rotate(swordRotation);
        ctx.scale(swordScale, swordScale);

        const img = getSprite(CONFIG.paths.sword);
        if (img.complete && img.naturalWidth > 0) {
            if (glowIntensity > 0) {
                // Secondary magical glow layer
                ctx.save();
                ctx.shadowColor = '#00FFFF';
                ctx.shadowBlur = Math.min(60, glowIntensity);
                ctx.globalCompositeOperation = 'screen';
                ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2, img.naturalWidth, img.naturalHeight);
                ctx.restore();
            }

            // Base shadow
            ctx.shadowColor = 'rgba(0,0,0,0.6)';
            ctx.shadowBlur = 15;
            ctx.shadowOffsetY = 10;

            // Draw Main Image
            ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2, img.naturalWidth, img.naturalHeight);
        }

        // Action flash overlay
        if (d.phase === 'STRIKE' || d.phase === 'SHOCKWAVE') {
            const strikeP = d.phase === 'STRIKE'
                ? (eff.t - tGather) / CONFIG.strikeDuration
                : 1 - ((eff.t - tStrike) / CONFIG.shockwaveDuration);

            ctx.globalCompositeOperation = 'screen';
            ctx.globalAlpha = Math.max(0, strikeP);
            if (img.complete && img.naturalWidth > 0) {
                ctx.save();
                ctx.shadowColor = '#E0FFFF';
                ctx.shadowBlur = 40;
                ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2, img.naturalWidth, img.naturalHeight);
                ctx.restore();
            }
        }

        ctx.restore();
    }

    // ==========================================
    // RENDER: SHOCKWAVE / RUNES
    // ==========================================
    if (d.phase === 'SHOCKWAVE' || d.phase === 'DISSIPATE') {
        const shockT = eff.t - tStrike;
        if (shockT < 800) {
            const p = shockT / 800;
            const easeOut = 1 - Math.pow(1 - p, 3);

            ctx.save();
            ctx.globalAlpha = (1 - p);
            ctx.translate(eff.tx, eff.ty);
            ctx.scale(1, 0.5); // Perspective

            // Expanding aura rings
            ctx.beginPath();
            ctx.arc(0, 0, easeOut * 250 * CONFIG.scale, 0, Math.PI * 2);
            ctx.fillStyle = 'rgba(0, 255, 255, 0.15)';
            ctx.fill();

            ctx.beginPath();
            ctx.arc(0, 0, easeOut * 250 * CONFIG.scale, 0, Math.PI * 2);
            ctx.strokeStyle = '#00FFFF';
            ctx.lineWidth = 10 * (1 - p);
            ctx.stroke();

            // Inner gold ring
            ctx.beginPath();
            ctx.arc(0, 0, easeOut * 150 * CONFIG.scale, 0, Math.PI * 2);
            ctx.strokeStyle = '#FFE066';
            ctx.lineWidth = 5 * (1 - p);
            ctx.stroke();

            // Runic/angular slash lines
            ctx.beginPath();
            for (let i = 0; i < 6; i++) {
                const angle = (i / 6) * Math.PI * 2;
                const inner = easeOut * 40;
                const outer = easeOut * 280 * (0.8 + Math.random() * 0.4);
                ctx.moveTo(Math.cos(angle) * inner, Math.sin(angle) * inner);
                ctx.lineTo(Math.cos(angle) * outer, Math.sin(angle) * outer);
            }
            ctx.strokeStyle = '#E0FFFF';
            ctx.lineWidth = 3 * (1 - p);
            ctx.stroke();

            ctx.restore();
        }
    }

    // Render leaves / embers
    if (d.phase === 'DISSIPATE' || d.phase === 'SHOCKWAVE') {
        d.leaves.forEach(e => {
            e.update(dt);
            e.draw(ctx);
        });
    }

    ctx.restore();

    return eff.t < tTotal;
};
